import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  Building2,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
} from 'lucide-react';
import api from '../services/api';
import { Customer, PaginatedResponse } from '../types';
import Layout from '../components/layout/Layout';

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 0,
    limit: 20,
  });
  const [activeMenu, setActiveMenu] = useState<number | null>(null);

  useEffect(() => {
    fetchCustomers();
  }, [page, search]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search }),
      });

      const response = await api.get<PaginatedResponse<Customer>>(`/customers?${params}`);
      if (response.data.success) {
        setCustomers(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchCustomers();
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this customer?')) return;

    try {
      await api.delete(`/customers/${id}`);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to delete customer:', error);
    }
    setActiveMenu(null);
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Users className="text-primary-500" size={28} />
            <h1 className="text-2xl font-semibold">Customers</h1>
          </div>
          <button
            onClick={() => navigate('/customers/new')}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Customer
          </button>
        </div>

        {/* Search */}
        <div className="card mb-6">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" size={20} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customers by name, email, or account code..."
                className="input pl-10 w-full"
              />
            </div>
            <button type="submit" className="btn btn-secondary">
              Search
            </button>
          </form>
        </div>

        {/* Customer List */}
        <div className="card">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 text-dark-muted">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p>No customers found</p>
              {search && (
                <button
                  onClick={() => {
                    setSearch('');
                    setPage(1);
                  }}
                  className="text-primary-500 hover:text-primary-400 mt-2"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-border">
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Customer</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Contact</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Location</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Status</th>
                      <th className="text-right py-3 px-4 font-medium text-dark-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr
                        key={customer.id}
                        className="border-b border-dark-border/50 hover:bg-dark-elevated/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-500">
                              <Building2 size={20} />
                            </div>
                            <div>
                              <p className="font-medium">{customer.name}</p>
                              {customer.trading_name && (
                                <p className="text-sm text-dark-muted">t/a {customer.trading_name}</p>
                              )}
                              {customer.account_code && (
                                <p className="text-xs text-dark-muted font-mono">{customer.account_code}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="space-y-1">
                            {customer.email && (
                              <div className="flex items-center gap-2 text-sm">
                                <Mail size={14} className="text-dark-muted" />
                                <span>{customer.email}</span>
                              </div>
                            )}
                            {customer.phone && (
                              <div className="flex items-center gap-2 text-sm">
                                <Phone size={14} className="text-dark-muted" />
                                <span>{customer.phone}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {customer.billing_city && (
                            <span className="text-sm">
                              {customer.billing_city}
                              {customer.billing_province && `, ${customer.billing_province}`}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              customer.is_active
                                ? 'bg-status-green/10 text-status-green'
                                : 'bg-status-red/10 text-status-red'
                            }`}
                          >
                            {customer.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setActiveMenu(activeMenu === customer.id ? null : customer.id)}
                              className="p-2 hover:bg-dark-elevated rounded-lg transition-colors"
                            >
                              <MoreVertical size={16} />
                            </button>
                            {activeMenu === customer.id && (
                              <div className="absolute right-0 top-full mt-1 w-40 bg-dark-surface border border-dark-border rounded-lg shadow-xl z-10">
                                <button
                                  onClick={() => {
                                    navigate(`/customers/${customer.id}`);
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-dark-elevated transition-colors"
                                >
                                  <Eye size={16} />
                                  View
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/customers/${customer.id}/edit`);
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-dark-elevated transition-colors"
                                >
                                  <Edit size={16} />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(customer.id)}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-status-red hover:bg-dark-elevated transition-colors"
                                >
                                  <Trash2 size={16} />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-dark-border mt-4">
                  <p className="text-sm text-dark-muted">
                    Showing {(page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} customers
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="p-2 rounded-lg hover:bg-dark-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="text-sm">
                      Page {page} of {pagination.total_pages}
                    </span>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === pagination.total_pages}
                      className="p-2 rounded-lg hover:bg-dark-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
